import PublicLayout from "@/components/PublicLayout";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { linkifyScripture } from "@/lib/scripture";
import { Send, Sparkles, Plus, MessageSquare, Trash2, ListPlus } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const SUGGESTIONS = [
  "Help me discover my sins.",
  "I keep struggling with anger toward my family.",
  "Help me understand forgiveness from the Bible.",
  "I feel ashamed about a recurring sin.",
  "Teach me to pray more honestly.",
  "I am tempted right now — pray with me.",
];

// Categories Grace can suggest tracking. Keep in sync with backend sin_categories.
const VALID_CATEGORIES = new Set([
  "Anger", "Lust", "Lying", "Pride", "Greed",
  "Addiction", "Jealousy", "Gossip", "Hatred",
  "Envy", "Sloth", "Gluttony", "Other",
]);

const TRACKER_TAG_RE = /\[\+\s*([A-Za-z]+)\s*\]/g;

function extractTrackerSuggestions(content) {
  if (typeof content !== "string") return { clean: content, categories: [] };
  const found = new Set();
  let match;
  const re = new RegExp(TRACKER_TAG_RE.source, "g");
  while ((match = re.exec(content)) !== null) {
    const cat = match[1].trim();
    if (VALID_CATEGORIES.has(cat)) found.add(cat);
  }
  const clean = content.replace(TRACKER_TAG_RE, "").replace(/\n{3,}/g, "\n\n").trim();
  return { clean, categories: Array.from(found) };
}

const MD_COMPONENTS = {
  p: ({ children, ...props }) => (
    <p className="mb-3 last:mb-0 leading-relaxed" {...props}>
      {linkifyScripture(children)}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-brand-ink" {...props}>
      {linkifyScripture(children)}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic text-brand-skyDark" {...props}>
      {linkifyScripture(children)}
    </em>
  ),
  ul: (props) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1.5" {...props} />,
  ol: (props) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1.5" {...props} />,
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {linkifyScripture(children)}
    </li>
  ),
  h1: (props) => <h3 className="font-serif text-xl text-brand-ink mt-2 mb-2" {...props} />,
  h2: (props) => <h3 className="font-serif text-lg text-brand-ink mt-2 mb-2" {...props} />,
  h3: (props) => <h3 className="font-serif text-base text-brand-ink mt-2 mb-2" {...props} />,
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="border-l-2 border-brand-sky pl-3 italic text-brand-quiet my-3 scripture-quote"
      {...props}
    >
      {linkifyScripture(children)}
    </blockquote>
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-white text-brand-ink text-xs" {...props} />
    ) : (
      <code className="block p-3 rounded-lg bg-white text-brand-ink text-xs my-2 overflow-x-auto" {...props} />
    ),
  a: (props) => (
    <a className="text-brand-skyDark underline hover:text-brand-ink" target="_blank" rel="noreferrer" {...props} />
  ),
  hr: () => <hr className="my-3 border-brand-line" />,
};

export default function AiChat() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data.items);
    } catch (_e) { /* ignore */ }
  };

  const loadMessages = async (sid) => {
    try {
      const { data } = await api.get(`/chat/sessions/${sid}/messages`);
      setMessages(data.items);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
    else setMessages([]);
  }, [activeId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempId, role: "user", content: msg, created_at: new Date().toISOString() },
    ]);
    try {
      const { data } = await api.post("/chat/send", {
        message: msg,
        session_id: activeId,
      });
      setActiveId(data.session_id);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempId),
        data.user_message,
        data.assistant_message,
      ]);
      loadSessions();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    }
    setSending(false);
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
  };

  const removeSession = async (sid) => {
    try {
      await api.delete(`/chat/sessions/${sid}`);
      if (activeId === sid) newSession();
      loadSessions();
    } catch (_e) {
      toast.error("Could not delete session.");
    }
  };

  const addToTracker = (category) => {
    navigate(`/sin-tracker?prefill=${encodeURIComponent(category)}`);
  };

  return (
    <PublicLayout hideFooter>
      <section className="bg-brand-base">
        <div className="max-w-7xl mx-auto px-3 lg:px-6 py-6 grid lg:grid-cols-12 gap-5 min-h-[calc(100vh-8rem)]">
          {/* SIDEBAR */}
          <aside className="lg:col-span-3 rounded-2xl bg-white border border-brand-line p-4 h-fit lg:sticky lg:top-20">
            <button
              onClick={newSession}
              data-testid="chat-new-session-button"
              className="w-full rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-4 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} /> New conversation
            </button>
            <p className="mt-5 text-[11px] uppercase tracking-widest text-brand-muted px-1">History</p>
            <ul className="mt-2 space-y-1 max-h-[55vh] overflow-y-auto pr-1">
              {sessions.length === 0 && (
                <li className="text-sm text-brand-quiet px-2 py-3">
                  Your past conversations will appear here.
                </li>
              )}
              {sessions.map((s) => (
                <li
                  key={s.id}
                  data-testid={`chat-session-${s.id}`}
                  className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm ${
                    activeId === s.id ? "bg-brand-mist text-brand-ink" : "text-brand-quiet hover:bg-brand-base"
                  }`}
                  onClick={() => setActiveId(s.id)}
                >
                  <MessageSquare className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
                  <span className="flex-1 truncate">{s.title || "Conversation"}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSession(s.id);
                    }}
                    data-testid={`chat-session-delete-${s.id}`}
                    className="opacity-0 group-hover:opacity-100 text-brand-muted hover:text-brand-setback"
                    aria-label="Delete conversation"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* CHAT */}
          <div className="lg:col-span-9 rounded-2xl bg-white border border-brand-line flex flex-col overflow-hidden">
            <div className="px-5 sm:px-7 py-5 border-b border-brand-line flex items-center gap-3 bg-gradient-to-r from-white to-brand-mist/60">
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage text-white grid place-items-center">
                <Sparkles className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <p className="font-serif text-2xl text-brand-ink leading-none">Grace</p>
                <p className="text-xs text-brand-quiet mt-0.5">Your Christian AI companion</p>
              </div>
            </div>

            <div className="flex-1 px-5 sm:px-7 py-6 space-y-5 overflow-y-auto" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center max-w-xl mx-auto py-10">
                  <p className="scripture-quote text-3xl text-brand-ink leading-snug">
                    “Come to Me, all you who labor and are heavy laden, and I will give you rest.”
                  </p>
                  <p className="text-xs text-brand-muted mt-2">— Matthew 11:28</p>
                  <p className="mt-8 text-brand-quiet">
                    Share what is on your heart. Grace will respond gently, from the Bible.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`chat-suggestion-${i}`}
                        className={`text-sm rounded-full px-3 py-1.5 transition-colors border ${
                          i === 0
                            ? "bg-brand-sky text-white border-brand-sky hover:bg-brand-skyDark"
                            : "bg-brand-base border-brand-line text-brand-quiet hover:text-brand-ink hover:bg-brand-mist"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  role={m.role}
                  content={m.content}
                  onAddToTracker={addToTracker}
                />
              ))}

              {sending && <MessageBubble role="assistant" content="…" pending />}

              <div ref={endRef} />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="px-5 sm:px-7 py-4 border-t border-brand-line bg-white"
              data-testid="chat-form"
            >
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  data-testid="chat-input"
                  rows={1}
                  placeholder="Share with Grace…"
                  className="flex-1 resize-none rounded-2xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink min-h-[48px] max-h-32"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  data-testid="chat-send-button"
                  className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors p-3 disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" strokeWidth={1.7} />
                </button>
              </div>
              <p className="text-[11px] text-brand-muted mt-2">
                Grace draws from the Bible and the teachings of Jesus. Tap a scripture reference to open it.
              </p>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function MessageBubble({ role, content, pending, onAddToTracker }) {
  const isUser = role === "user";
  const { clean, categories } = isUser
    ? { clean: content, categories: [] }
    : extractTrackerSuggestions(content);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-brand-sky text-white rounded-br-md whitespace-pre-wrap"
            : "bg-brand-mist text-brand-ink rounded-bl-md"
        }`}
      >
        {pending ? (
          <span className="inline-flex gap-1.5 items-center">
            <Dot delay="0s" />
            <Dot delay="0.15s" />
            <Dot delay="0.3s" />
          </span>
        ) : isUser ? (
          content
        ) : (
          <>
            <div className="grace-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {clean}
              </ReactMarkdown>
            </div>
            {categories.length > 0 && (
              <div
                className="mt-3 pt-3 border-t border-brand-line/70 flex flex-wrap gap-2"
                data-testid="chat-tracker-suggestions"
              >
                <span className="text-[11px] uppercase tracking-widest text-brand-muted self-center mr-1">
                  Add to sin tracker:
                </span>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => onAddToTracker && onAddToTracker(cat)}
                    data-testid={`chat-add-sin-${cat.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-brand-skyDark border border-brand-sky/40 hover:bg-brand-sky hover:text-white transition-colors px-3 py-1.5 text-xs font-medium"
                  >
                    <ListPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-brand-quiet inline-block animate-bounce"
      style={{ animationDelay: delay }}
    />
  );
}
